import { Suspense, lazy } from "react";
import { Toaster, SonnerToaster } from "@lyder-talk/ui";
import { TooltipProvider } from "@lyder-talk/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, createQueryClient } from "@lyder-talk/core";

// Layout
import MarketingLayout from "./components/layout/MarketingLayout";

// Pages
import Landing from "./pages/Landing";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Features from "./pages/Features";
import NotFound from "./pages/NotFound";

// Lazy load auth pages
const Auth = lazy(() => import("./pages/Auth"));
const AuthForward = lazy(() => import("./pages/AuthForward"));

// Lazy load updates pages
const ProductUpdates = lazy(() => import("./pages/ProductUpdates"));
const UpdateDetail = lazy(() => import("./pages/UpdateDetail"));

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = createQueryClient();

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <SonnerToaster />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Marketing pages with shared layout */}
                  <Route element={<MarketingLayout />}>
                    <Route path="/" element={<Landing />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/updates" element={<ProductUpdates />} />
                    <Route path="/updates/:updateId" element={<UpdateDetail />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                  </Route>

                  {/* Auth routes */}
                  <Route path="/auth" element={<Navigate to="/auth/signin" replace />} />
                  <Route path="/auth/:mode" element={<Auth />} />
                  {/* Auth email links live on the app subdomain now — forward, keeping the token. */}
                  <Route path="/auth/invite" element={<AuthForward />} />
                  <Route path="/auth/confirm" element={<AuthForward />} />
                  <Route path="/auth/reset-password" element={<AuthForward />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
